// Logic của bộ chọn dịch vụ trên trang đặt lịch khách: gom bảng giá công khai
// thành nhóm → gói (cặp "có make"/"không make" gộp làm một, tick chọn), đúng
// cách form đơn của sale làm (CEO chốt 20/08/2026).
//
// Tách khỏi ServicePicker.tsx vì Radix Select không mở được trong jsdom — phần
// quyết định gói nào, mục đích nào còn sống sau khi đổi gói phải test được.

import { buildPackageOptions, type PackageOption } from "@/features/products/packageOptions";
import type { PublicBookingService } from "./api";

/** Thứ tự nhóm cố định, giống thứ tự trong form đơn nội bộ. */
const CATEGORY_ORDER = ["profile", "idphoto", "corporate"] as const;

/** Tên nhóm khi bảng giá công khai không có gói nào của nhóm đó để lấy tên. */
const CATEGORY_FALLBACK_NAME: Record<string, string> = {
  profile: "Profile Chuyên Nghiệp",
  idphoto: "Ảnh Thẻ",
  corporate: "Ảnh Doanh Nghiệp",
};

export type ServiceGroup = {
  category: string;
  name: string;
  options: PackageOption[];
  /** Nhóm chỉ nhận báo giá riêng (doanh nghiệp) — không có gói để đặt online. */
  quoteOnly: boolean;
};

/**
 * Gom bảng giá thành nhóm theo thứ tự cố định.
 *
 * Nhóm doanh nghiệp LUÔN hiện dù bảng giá công khai không trả gói nào: đó là
 * nhóm bán theo báo giá riêng, bấm vào ra khối liên hệ. Nhóm lạ (admin thêm
 * danh mục mới) xếp cuối chứ không bị bỏ rơi.
 */
export function buildServiceGroups(services: PublicBookingService[]): ServiceGroup[] {
  const byCategory = new Map<string, PublicBookingService[]>();
  for (const service of services) {
    const list = byCategory.get(service.category);
    if (list) list.push(service);
    else byCategory.set(service.category, [service]);
  }
  const categories = [
    ...CATEGORY_ORDER,
    ...[...byCategory.keys()].filter((c) => !CATEGORY_ORDER.includes(c as (typeof CATEGORY_ORDER)[number])),
  ];
  const out: ServiceGroup[] = [];
  for (const category of categories) {
    const list = byCategory.get(category) ?? [];
    if (list.length === 0 && category !== "corporate") continue;
    out.push({
      category,
      name: list[0]?.categoryName ?? CATEGORY_FALLBACK_NAME[category] ?? category,
      options: buildPackageOptions(
        list.map((service) => ({
          id: service.code,
          name: service.name,
          price: service.price,
          ...(service.purposes ? { purposes: service.purposes } : {}),
        })),
      ),
      quoteOnly: list.length === 0,
    });
  }
  return out;
}

/**
 * Mục đích chụp còn lại sau khi khách đổi gói (hoặc tick makeup).
 *
 * Gói mới còn đúng mục đích cũ thì giữ — khách chọn "Hộ chiếu" rồi tick makeup
 * không phải chọn lại, và GIÁ tự lấy theo bảng của gói mới. Gói mới chỉ có một
 * mục đích thì chọn sẵn cho đỡ một cú bấm. Còn lại trả "" để bắt chọn lại,
 * tuyệt đối không giữ mục đích của gói khác (giá sẽ sai).
 */
export function nextPurposeId(
  service: PublicBookingService | null,
  currentPurposeId: string,
): string {
  const purposes = service?.purposes ?? [];
  if (purposes.length === 0) return "";
  if (purposes.length === 1) return purposes[0]?.id ?? "";
  return purposes.some((p) => p.id === currentPurposeId) ? currentPurposeId : "";
}

/**
 * Kiểu ảnh (concept) còn lại sau khi khách đổi gói.
 *
 * Khác mục đích chụp ở một điểm: gói chỉ có MỘT concept cũng KHÔNG chọn sẵn.
 * Concept là tuỳ chọn — chọn hộ khách là đơn ghi một kiểu ảnh khách chưa từng
 * đồng ý. Gói mới không có concept cũ thì trả "" để khách chọn lại từ đầu.
 */
export function nextConceptId(
  service: PublicBookingService | null,
  currentConceptId: string,
): string {
  if (!currentConceptId) return "";
  const concepts = service?.concepts ?? [];
  return concepts.some((c) => c.id === currentConceptId) ? currentConceptId : "";
}

/** Tra dịch vụ theo mã — dùng khi đổi biến thể make / chọn gói trong danh sách. */
export function serviceByCode(
  services: PublicBookingService[],
  code: string | undefined,
): PublicBookingService | null {
  if (!code) return null;
  return services.find((s) => s.code === code) ?? null;
}
